import { isLocalHost } from 'src/app/models/util.model';

export type ExceptionSource = 'global_handler' | 'bootstrap';
export type ExceptionTelemetryLocation = Pick<Location, 'hostname' | 'origin'> & Partial<Pick<Location, 'pathname' | 'search'>>;

const recentlyReported = new WeakMap<object, number>();
const duplicateWindowMs = 1000;
const parameterLength = 100;

function redactDetails(text: string): string {
  return text
    .replace(/https?:\/\/[^\s)]+/gi, value => {
      try {
        const url = new URL(value);
        return url.origin + url.pathname;
      } catch {
        return '[url]';
      }
    })
    .replace(/\bBearer\s+[^\s,;]+/gi, 'Bearer [redacted]')
    .replace(/\b(?:access_token|refresh_token|id_token|password|authorization|api_key|apikey|secret)\s*[:=]\s*[^\s&,;]+/gi, '[credential redacted]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]');
}

function addDetailChunks(parameters: Record<string, unknown>, name: string, text: string, chunks: number): void {
  const characters = Array.from(text);
  for (let index = 0; index < chunks && index * parameterLength < characters.length; index++) {
    parameters[index === 0 ? name : `${name}_${index + 1}`] =
      characters.slice(index * parameterLength, (index + 1) * parameterLength).join('');
  }
  parameters[`${name}_truncated`] = characters.length > chunks * parameterLength;
}

function fingerprint(text: string): string {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index++) {
    hash = Math.imul(hash ^ text.charCodeAt(index), 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function unwrapError(value: unknown): unknown {
  const visited = new Set<unknown>();
  while (value && typeof value === 'object' && !visited.has(value)) {
    visited.add(value);
    if ('ngOriginalError' in value && value.ngOriginalError != null) {
      value = value.ngOriginalError;
    } else if (value instanceof ErrorEvent && value.error != null) {
      value = value.error;
    } else if (value instanceof PromiseRejectionEvent) {
      value = value.reason;
    } else {
      break;
    }
  }
  return value;
}

/** Shared by Angular's handler and the bootstrap catch, without depending on app state. */
export function reportGlobalException(
  original: unknown,
  source: ExceptionSource,
  location: ExceptionTelemetryLocation = window.location,
  isPreviewUser = false
): void {
  // Preserve developer diagnostics even when reporting is unavailable.
  console.error(original);
  if (isLocalHost(location.hostname) || isPreviewUser) {
    return;
  }

  let value: unknown;
  try {
    value = unwrapError(original);
  } catch {
    value = original;
  }

  // Browser listeners and Angular can forward the same exception. Allow later recurrences.
  const identity = value && typeof value === 'object' ? value : original;
  if (identity && typeof identity === 'object') {
    const now = Date.now();
    const previous = recentlyReported.get(identity);
    if (previous !== undefined && now - previous < duplicateWindowMs) {
      return;
    }
    recentlyReported.set(identity, now);
  }

  // A blocked or unavailable GA script must not interrupt the application.
  try {
    const gtag = (window as Window & {
      gtag?: (command: string, name: string, parameters: Record<string, unknown>) => void;
    }).gtag;
    if (typeof gtag === 'function') {
      const details = value && typeof value === 'object' ? value as Record<string, unknown> : {};
      const message = redactDetails(typeof details['message'] === 'string' ? details['message'] :
        typeof value === 'string' ? value : 'Unhandled non-Error exception');
      // Do not manufacture a stack here: it would identify the reporter, not the failure.
      const stack = redactDetails(typeof details['stack'] === 'string' ? details['stack'] : '');
      const errorType = redactDetails(typeof details['name'] === 'string' ? details['name'] : 'Error');
      const parameters: Record<string, unknown> = {
        description: Array.from(message || errorType).slice(0, parameterLength).join(''),
        error_type: Array.from(errorType).slice(0, parameterLength).join(''),
        error_source: source,
        error_fingerprint: fingerprint(`${errorType}\n${message}\n${stack}`),
        fatal: source === 'bootstrap',
        // Preserve the page path while excluding query strings, fragments and emails.
        page_location: redactDetails(location.origin + (location.pathname || '/')).slice(0, 1000),
        page_referrer: '',
        page_title: 'BindTuning Admin App'
      };
      // Keep the event under GA's 25 parameter limit and each detail value within 100 characters.
      addDetailChunks(parameters, 'error_message', message, 3);
      addDetailChunks(parameters, 'error_stack', stack, 8);
      const cause = details['cause'];
      if (cause instanceof Error || typeof cause === 'string') {
        parameters['error_cause'] = Array.from(redactDetails(
          typeof cause === 'string' ? cause : `${cause.name}: ${cause.message}`
        )).slice(0, parameterLength).join('');
      } else if (cause instanceof ErrorEvent) {
        parameters['error_cause'] = Array.from(
          `${redactDetails(cause.filename)}:${cause.lineno}:${cause.colno}`
        ).slice(0, parameterLength).join('');
      }
      if (typeof details['code'] === 'string' || typeof details['code'] === 'number') {
        parameters['error_code'] = Array.from(redactDetails(String(details['code']))).slice(0, parameterLength).join('');
      }
      gtag('event', 'exception', parameters);
    }
  } catch {
    // Telemetry must never become another unhandled exception.
  }
}
